import { useState } from "react";
import { Send } from "lucide-react";
import type { VideoType } from "./video-type-picker";

interface VideoFormProps {
  type: VideoType;
  onSubmit: (inputs: VideoFormInputs) => void;
  isSubmitting: boolean;
  onBack: () => void;
}

export interface VideoFormInputs {
  topic?: string;
  language: string;
  durationSeconds: number;
  brandColor?: string;
  websiteUrl?: string;
  customInstructions?: string;
}

const TYPE_LABELS: Record<VideoType, string> = {
  explainer: "Explainer Video",
  "product-launch": "Product Launch",
  testimonials: "Testimoniale",
  "data-viz": "Data Viz",
};

export default function VideoForm({ type, onSubmit, isSubmitting, onBack }: VideoFormProps) {
  const [topic, setTopic] = useState("");
  const [duration, setDuration] = useState(30);
  const [brandColor, setBrandColor] = useState("#ef3e29");
  const [customInstructions, setCustomInstructions] = useState("");
  const [language, setLanguage] = useState("pl");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      topic: topic.trim() || undefined,
      language,
      durationSeconds: duration,
      brandColor,
      customInstructions: customInstructions.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-white/80">{TYPE_LABELS[type]}</h3>
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-white/40 hover:text-white/60 transition"
        >
          ← Zmień typ
        </button>
      </div>

      {/* Topic */}
      <div>
        <label className="block text-xs text-white/50 mb-1.5">Temat filmu</label>
        <input
          type="text"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="np. Jak AI zmienia marketing w 2025"
          className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-white/20 focus:border-[#ef3e29]/40 focus:outline-none transition"
          maxLength={500}
        />
      </div>

      {/* Duration */}
      <div>
        <label className="block text-xs text-white/50 mb-1.5">Czas trwania: {duration}s</label>
        <input
          type="range"
          min={10}
          max={90}
          step={5}
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
          className="w-full accent-[#ef3e29]"
        />
        <div className="flex justify-between text-[10px] text-white/20 mt-1">
          <span>10s</span>
          <span>90s</span>
        </div>
      </div>

      {/* Language & Color row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-white/50 mb-1.5">Język</label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-sm focus:border-[#ef3e29]/40 focus:outline-none"
          >
            <option value="pl">Polski</option>
            <option value="en">English</option>
            <option value="de">Deutsch</option>
            <option value="es">Español</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-white/50 mb-1.5">Kolor marki</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
              className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent"
            />
            <span className="text-xs text-white/30 font-mono">{brandColor}</span>
          </div>
        </div>
      </div>

      {/* Custom instructions */}
      <div>
        <label className="block text-xs text-white/50 mb-1.5">
          Dodatkowe instrukcje (opcjonalne)
        </label>
        <textarea
          value={customInstructions}
          onChange={(e) => setCustomInstructions(e.target.value)}
          placeholder="np. Skup się na statystykach, dodaj CTA na końcu..."
          rows={3}
          className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-white/20 focus:border-[#ef3e29]/40 focus:outline-none resize-none transition"
          maxLength={1000}
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting}
        className={`
          w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition
          ${
            isSubmitting
              ? "bg-white/10 text-white/40 cursor-wait"
              : "bg-[#ef3e29] hover:bg-[#ef3e29]/80 text-white cursor-pointer"
          }
        `}
      >
        <Send size={16} />
        {isSubmitting ? "Generuję..." : "Generuj wideo"}
      </button>
    </form>
  );
}
